import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Pencil, Target, GitBranch, ClipboardList, AlertTriangle, StickyNote, ListChecks } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  not_started: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  open: "bg-rose-50 text-rose-700 border-rose-200",
  mitigated: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
};

const SEVERITY = { low: "text-emerald-600", medium: "text-amber-600", high: "text-rose-600", critical: "text-rose-700 font-semibold" };

function useCrud(pid, path) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/projects/${pid}/${path}`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);
  const create = (data) => api.post(`/projects/${pid}/${path}`, data).then(load);
  const patch = (rid, data) => api.patch(`/projects/${pid}/${path}/${rid}`, data).then(load);
  const remove = (rid) => api.delete(`/projects/${pid}/${path}/${rid}`).then(load);
  return { rows, load, create, patch, remove };
}

export default function PlanningTab({ pid, project }) {
  const phases = useCrud(pid, "phases");
  const milestones = useCrud(pid, "milestones");
  const deliverables = useCrud(pid, "deliverables");
  const dependencies = useCrud(pid, "dependencies");
  const risks = useCrud(pid, "risks");
  const notes = useCrud(pid, "plan-notes");
  const [employees, setEmployees] = useState([]);
  useEffect(() => { api.get("/employees").then(r => setEmployees(r.data)).catch(()=>{}); }, []);
  const empName = (id) => employees.find(e => e.id === id)?.name || "";

  const [phaseForm, setPhaseForm] = useState({ name: "", sequence: 1, start_date: "", end_date: "", progress: 0, status: "not_started", notes: "", assigned_to: "" });
  const [milestoneForm, setMilestoneForm] = useState({ title: "", target_date: "", status: "pending", phase_id: "", notes: "", assigned_to: "" });
  const [deliverableForm, setDeliverableForm] = useState({ title: "", type: "Document", due_date: "", status: "pending", notes: "" });
  const [riskForm, setRiskForm] = useState({ title: "", severity: "medium", probability: "medium", mitigation: "", status: "open" });
  const [noteText, setNoteText] = useState("");
  const [depForm, setDepForm] = useState({ from_id: "", to_id: "", kind: "finish_to_start", notes: "" });

  const addPhase = async () => {
    if (!phaseForm.name.trim()) return toast.error("Phase name required");
    try { await phases.create(phaseForm); toast.success("Phase added"); setPhaseForm({ name: "", sequence: (phases.rows.length||0)+2, start_date: "", end_date: "", progress: 0, status: "not_started", notes: "", assigned_to: "" }); }
    catch { toast.error("Failed"); }
  };
  const updatePhaseProgress = (p, progress) => phases.patch(p.id, { progress, status: progress >= 100 ? "completed" : progress > 0 ? "in_progress" : "not_started" });

  const addMilestone = async () => {
    if (!milestoneForm.title.trim()) return toast.error("Title required");
    try { await milestones.create(milestoneForm); toast.success("Milestone added"); setMilestoneForm({ title: "", target_date: "", status: "pending", phase_id: "", notes: "", assigned_to: "" }); }
    catch { toast.error("Failed"); }
  };
  const addDeliverable = async () => {
    if (!deliverableForm.title.trim()) return toast.error("Title required");
    try { await deliverables.create(deliverableForm); toast.success("Deliverable added"); setDeliverableForm({ title: "", type: "Document", due_date: "", status: "pending", notes: "" }); }
    catch { toast.error("Failed"); }
  };
  const addRisk = async () => {
    if (!riskForm.title.trim()) return toast.error("Title required");
    try { await risks.create(riskForm); toast.success("Risk logged"); setRiskForm({ title: "", severity: "medium", probability: "medium", mitigation: "", status: "open" }); }
    catch { toast.error("Failed"); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await notes.create({ text: noteText, category: "general" }); toast.success("Note added"); setNoteText(""); }
    catch { toast.error("Failed"); }
  };
  const addDep = async () => {
    if (!depForm.from_id || !depForm.to_id) return toast.error("Select both works");
    if (depForm.from_id === depForm.to_id) return toast.error("Cannot depend on itself");
    try { await dependencies.create({ ...depForm, entity: "work" }); toast.success("Dependency added"); setDepForm({ from_id: "", to_id: "", kind: "finish_to_start", notes: "" }); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="planning-tab">
      {project?.goals && (
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2 flex items-center gap-2"><Target className="w-3.5 h-3.5" /> Project Goals</div>
          <p className="text-sm">{project.goals}</p>
        </div>
      )}

      {/* Phases */}
      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="phases-section">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-primary" /><h3 className="font-display text-lg">Phases</h3><span className="text-xs text-muted-foreground">({phases.rows.length})</span></div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-7 gap-2 bg-muted/20">
          <Input placeholder="Phase name" value={phaseForm.name} onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })} data-testid="phase-name-input" />
          <Input type="number" placeholder="Seq" value={phaseForm.sequence} onChange={(e) => setPhaseForm({ ...phaseForm, sequence: Number(e.target.value) })} />
          <Input type="date" value={phaseForm.start_date} onChange={(e) => setPhaseForm({ ...phaseForm, start_date: e.target.value })} />
          <Input type="date" value={phaseForm.end_date} onChange={(e) => setPhaseForm({ ...phaseForm, end_date: e.target.value })} />
          <Input type="number" min="0" max="100" placeholder="%" value={phaseForm.progress} onChange={(e) => setPhaseForm({ ...phaseForm, progress: Number(e.target.value) })} />
          <Select value={phaseForm.assigned_to} onValueChange={(v) => setPhaseForm({ ...phaseForm, assigned_to: v })}>
            <SelectTrigger data-testid="phase-assignee-select"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={addPhase} data-testid="add-phase-btn" className="bg-primary text-primary-foreground"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
        <div className="divide-y divide-border/50">
          {phases.rows.map(p => (
            <div key={p.id} data-testid={`phase-${p.id}`} className="p-4 grid grid-cols-12 items-center gap-3">
              <div className="col-span-3">
                <div className="font-medium text-sm">{p.sequence}. {p.name}</div>
                {p.assigned_to && <div className="text-[11px] text-muted-foreground">Owner: {empName(p.assigned_to) || "—"}</div>}
                {p.notes && <div className="text-xs text-muted-foreground truncate">{p.notes}</div>}
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">{p.start_date || "—"} → {p.end_date || "—"}</div>
              <div className="col-span-2">
                <Select value={p.assigned_to || ""} onValueChange={(v) => phases.patch(p.id, { assigned_to: v })}>
                  <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Assign owner" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-3 space-y-1">
                <div className="flex justify-between text-[10px]"><span>Progress</span><span>{p.progress||0}%</span></div>
                <input type="range" min="0" max="100" step="5" value={p.progress||0} onChange={(e) => phases.patch(p.id, { progress: Number(e.target.value) })} className="w-full accent-primary" data-testid={`phase-progress-${p.id}`} />
              </div>
              <div className="col-span-1"><span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_COLOR[p.status] || "bg-muted"}`}>{p.status?.replace("_"," ")}</span></div>
              <div className="col-span-1 text-right"><button onClick={() => phases.remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
          ))}
          {!phases.rows.length && <div className="p-6 text-center text-sm text-muted-foreground">No phases yet.</div>}
        </div>
      </div>

      {/* Milestones + Deliverables side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="milestones-section">
          <div className="p-5 border-b border-border/50 flex items-center gap-2"><Target className="w-4 h-4 text-primary" /><h3 className="font-display text-lg">Milestones</h3><span className="text-xs text-muted-foreground">({milestones.rows.length})</span></div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-2 bg-muted/20">
            <Input className="md:col-span-2" placeholder="Milestone title" value={milestoneForm.title} onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })} data-testid="milestone-title-input" />
            <Input type="date" value={milestoneForm.target_date} onChange={(e) => setMilestoneForm({ ...milestoneForm, target_date: e.target.value })} />
            <Select value={milestoneForm.phase_id} onValueChange={(v) => setMilestoneForm({ ...milestoneForm, phase_id: v })}>
              <SelectTrigger><SelectValue placeholder="Link to phase" /></SelectTrigger>
              <SelectContent>{phases.rows.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={milestoneForm.assigned_to} onValueChange={(v) => setMilestoneForm({ ...milestoneForm, assigned_to: v })}>
              <SelectTrigger data-testid="milestone-assignee-select"><SelectValue placeholder="Responsible" /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addMilestone} data-testid="add-milestone-btn" className="bg-primary text-primary-foreground"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
          </div>
          <div className="divide-y divide-border/50 max-h-[360px] overflow-y-auto">
            {milestones.rows.map(m => (
              <div key={m.id} className="p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.title}</div>
                  <div className="text-xs text-muted-foreground">Target: {m.target_date || "—"}{m.assigned_to && ` · Owner: ${empName(m.assigned_to)}`}</div>
                </div>
                <Select value={m.status} onValueChange={(v) => milestones.patch(m.id, { status: v })}>
                  <SelectTrigger className={`h-7 w-32 text-[11px] capitalize ${STATUS_COLOR[m.status]||""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["pending","in_progress","done"].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => milestones.remove(m.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {!milestones.rows.length && <div className="p-6 text-center text-sm text-muted-foreground">No milestones yet.</div>}
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="deliverables-section">
          <div className="p-5 border-b border-border/50 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" /><h3 className="font-display text-lg">Deliverables</h3><span className="text-xs text-muted-foreground">({deliverables.rows.length})</span></div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-2 bg-muted/20">
            <Input className="md:col-span-2" placeholder="Deliverable" value={deliverableForm.title} onChange={(e) => setDeliverableForm({ ...deliverableForm, title: e.target.value })} data-testid="deliverable-title-input" />
            <Input type="date" value={deliverableForm.due_date} onChange={(e) => setDeliverableForm({ ...deliverableForm, due_date: e.target.value })} />
            <Select value={deliverableForm.type} onValueChange={(v) => setDeliverableForm({ ...deliverableForm, type: v })}>
              <SelectTrigger className="md:col-span-2"><SelectValue /></SelectTrigger>
              <SelectContent>{["Document","Drawing","Sample","Report","Approval","Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addDeliverable} data-testid="add-deliverable-btn" className="bg-primary text-primary-foreground"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
          </div>
          <div className="divide-y divide-border/50 max-h-[360px] overflow-y-auto">
            {deliverables.rows.map(d => (
              <div key={d.id} className="p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.title}</div>
                  <div className="text-xs text-muted-foreground">{d.type} · Due {d.due_date || "—"}</div>
                </div>
                <Select value={d.status} onValueChange={(v) => deliverables.patch(d.id, { status: v })}>
                  <SelectTrigger className={`h-7 w-32 text-[11px] capitalize ${STATUS_COLOR[d.status]||""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["pending","in_progress","done"].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => deliverables.remove(d.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {!deliverables.rows.length && <div className="p-6 text-center text-sm text-muted-foreground">No deliverables yet.</div>}
          </div>
        </div>
      </div>

      {/* Risks + Dependencies + Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="risks-section">
          <div className="p-4 border-b border-border/50 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-primary" /><h3 className="font-display text-base">Risks</h3><span className="text-xs text-muted-foreground">({risks.rows.length})</span></div>
          <div className="p-3 space-y-2 bg-muted/20">
            <Input placeholder="Risk title" value={riskForm.title} onChange={(e) => setRiskForm({ ...riskForm, title: e.target.value })} data-testid="risk-title-input" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={riskForm.severity} onValueChange={(v) => setRiskForm({ ...riskForm, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["low","medium","high","critical"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={riskForm.probability} onValueChange={(v) => setRiskForm({ ...riskForm, probability: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["low","medium","high"].map(s => <SelectItem key={s} value={s} className="capitalize">P: {s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Mitigation" rows={2} value={riskForm.mitigation} onChange={(e) => setRiskForm({ ...riskForm, mitigation: e.target.value })} />
            <Button onClick={addRisk} data-testid="add-risk-btn" className="bg-primary text-primary-foreground w-full">Add Risk</Button>
          </div>
          <div className="divide-y divide-border/50 max-h-[280px] overflow-y-auto">
            {risks.rows.map(r => (
              <div key={r.id} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-[11px] text-muted-foreground"><span className={`capitalize ${SEVERITY[r.severity]}`}>{r.severity}</span> · P: {r.probability}</div>
                    {r.mitigation && <div className="text-[11px] text-muted-foreground mt-1">Mitigation: {r.mitigation}</div>}
                  </div>
                  <button onClick={() => risks.remove(r.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <Select value={r.status} onValueChange={(v) => risks.patch(r.id, { status: v })}>
                  <SelectTrigger className={`h-6 w-full text-[11px] mt-1.5 capitalize ${STATUS_COLOR[r.status]||""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["open","mitigated","closed"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            {!risks.rows.length && <div className="p-6 text-center text-sm text-muted-foreground">No risks logged.</div>}
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="dependencies-section">
          <div className="p-4 border-b border-border/50 flex items-center gap-2"><GitBranch className="w-4 h-4 text-primary" /><h3 className="font-display text-base">Dependencies</h3><span className="text-xs text-muted-foreground">({dependencies.rows.length})</span></div>
          <div className="p-3 space-y-2 bg-muted/20">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Add task/work dependency</div>
            <Input placeholder="From work ID" value={depForm.from_id} onChange={(e) => setDepForm({ ...depForm, from_id: e.target.value })} />
            <Input placeholder="To work ID (must finish after)" value={depForm.to_id} onChange={(e) => setDepForm({ ...depForm, to_id: e.target.value })} />
            <Button onClick={addDep} data-testid="add-dep-btn" className="bg-primary text-primary-foreground w-full">Link</Button>
          </div>
          <div className="divide-y divide-border/50 max-h-[280px] overflow-y-auto">
            {dependencies.rows.map(d => (
              <div key={d.id} className="p-3 flex items-center justify-between gap-2">
                <div className="text-xs">
                  <span className="font-mono">{d.from_id.slice(-6)}</span> → <span className="font-mono">{d.to_id.slice(-6)}</span>
                  <div className="text-[10px] text-muted-foreground">{d.kind?.replace(/_/g," ")}</div>
                </div>
                <button onClick={() => dependencies.remove(d.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {!dependencies.rows.length && <div className="p-6 text-center text-sm text-muted-foreground">Link work items by ID from the Work List tab.</div>}
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="notes-section">
          <div className="p-4 border-b border-border/50 flex items-center gap-2"><StickyNote className="w-4 h-4 text-primary" /><h3 className="font-display text-base">Planning Notes</h3><span className="text-xs text-muted-foreground">({notes.rows.length})</span></div>
          <div className="p-3 space-y-2 bg-muted/20">
            <Textarea placeholder="Add a planning note…" rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="plan-note-input" />
            <Button onClick={addNote} data-testid="add-note-btn" className="bg-primary text-primary-foreground w-full">Add Note</Button>
          </div>
          <div className="divide-y divide-border/50 max-h-[280px] overflow-y-auto">
            {notes.rows.map(n => (
              <div key={n.id} className="p-3">
                <div className="text-xs">{n.text}</div>
                <div className="mt-1 flex items-center justify-between">
                  <div className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</div>
                  <button onClick={() => notes.remove(n.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
            {!notes.rows.length && <div className="p-6 text-center text-sm text-muted-foreground">No notes yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
